import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AnalyticsData } from "@/components/analytics/analyticsApiMappers";
import type {
  AnalyticsStat,
  BookingSource,
  FilterOption,
  RevenueBar,
  SpaceRevenue,
  TopCustomer,
} from "@/components/analytics/types";

/**
 * Analytics feature state (Figma 13.1), hydrated from the five analytics reads +
 * the venues list (the venue-filter dropdown). `rangeSlug` + `venueId` are the
 * header filters; changing either re-reads all widgets via `getAnalyticsAction`.
 */
export interface AnalyticsState {
  stats: AnalyticsStat[];
  monthlyRevenue: RevenueBar[];
  revenueTotal: string;
  revenueBySpace: SpaceRevenue[];
  bookingSources: BookingSource[];
  topCustomers: TopCustomer[];
  /** Venue-filter dropdown options (from `/venues/`). */
  venueOptions: FilterOption[];
  /** Selected venue id, or "all". */
  venueId: string;
  rangeSlug: string;
  /** Initial hydration in flight (first paint). */
  loading: boolean;
  /** Filter-driven refetch in flight — dims the widgets (no spinner). */
  refetching: boolean;
}

const initialState: AnalyticsState = {
  stats: [],
  monthlyRevenue: [],
  revenueTotal: "",
  revenueBySpace: [],
  bookingSources: [],
  topCustomers: [],
  venueOptions: [],
  venueId: "all",
  rangeSlug: "this_month",
  loading: true,
  refetching: false,
};

/** One-shot payload the Initializer hydrates. */
export interface AnalyticsHydratePayload {
  data: AnalyticsData;
  venueOptions: FilterOption[];
  rangeSlug: string;
}

/** Apply a fresh AnalyticsData payload (hydrate + refetch share this). */
function applyData(state: AnalyticsState, data: AnalyticsData) {
  state.stats = data.stats;
  state.monthlyRevenue = data.monthlyRevenue;
  state.revenueTotal = data.revenueBySpace.total;
  state.revenueBySpace = data.revenueBySpace.segments;
  state.bookingSources = data.bookingSources;
  state.topCustomers = data.topCustomers;
}

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    hydrateAnalytics(state, action: PayloadAction<AnalyticsHydratePayload>) {
      const p = action.payload;
      if (!p) return;
      if (p.data) applyData(state, p.data);
      state.venueOptions = p.venueOptions ?? [];
      if (p.rangeSlug) state.rangeSlug = p.rangeSlug;
    },
    /** Replace all widgets after a filter refetch. */
    setAnalyticsData(state, action: PayloadAction<AnalyticsData>) {
      if (action.payload) applyData(state, action.payload);
    },
    setRangeSlug(state, action: PayloadAction<string>) {
      state.rangeSlug = action.payload;
    },
    setVenueId(state, action: PayloadAction<string>) {
      state.venueId = action.payload;
    },
    setAnalyticsLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setAnalyticsRefetching(state, action: PayloadAction<boolean>) {
      state.refetching = action.payload;
    },
  },
});

export const {
  hydrateAnalytics,
  setAnalyticsData,
  setRangeSlug,
  setVenueId,
  setAnalyticsLoading,
  setAnalyticsRefetching,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
